import {
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentType,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Button, PageError, PageLoading, Toaster } from "@lumika/ui";
import { authApi } from "./api/auth";
import { ApiError } from "./api/client";
import {
  AppShell,
  defaultLandingForRole,
  primaryRoleForMemberships,
} from "./layouts/AppShell";
import { SelectDealershipPage } from "./pages/SelectDealershipPage";
import { POST_LOGIN_PATH_KEY, SignInPage } from "./pages/SignInPage";

// Keep the authenticated shell small and stable. Every route below owns a
// separate async boundary, so visiting Reports or Billing-style settings does
// not make an Inbox-first user download those modules during bootstrap.
function lazyRoute<TModule>(
  loader: () => Promise<TModule>,
  select: (module: TModule) => ComponentType,
) {
  const Component = lazy(async () => ({ default: select(await loader()) }));

  return function DeferredRoute() {
    return (
      <Suspense fallback={<RoutePending />}>
        <Component />
      </Suspense>
    );
  };
}

const ContactsLayout = lazyRoute(
  () => import("./layouts/ContactsLayout"),
  (module) => module.ContactsLayout,
);
const InboxLayout = lazyRoute(
  () => import("./layouts/InboxLayout"),
  (module) => module.InboxLayout,
);
const SettingsLayout = lazyRoute(
  () => import("./layouts/SettingsLayout"),
  (module) => module.SettingsLayout,
);
const AppointmentsPage = lazyRoute(
  () => import("./pages/AppointmentsPage"),
  (module) => module.AppointmentsPage,
);
const Contact360Page = lazyRoute(
  () => import("./pages/Contact360Page"),
  (module) => module.Contact360Page,
);
const HomePage = lazyRoute(
  () => import("./pages/HomePage"),
  (module) => module.HomePage,
);
const InsightsPage = lazyRoute(
  () => import("./pages/InsightsPage"),
  (module) => module.InsightsPage,
);
const LeadDetailPage = lazyRoute(
  () => import("./pages/LeadDetailPage"),
  (module) => module.LeadDetailPage,
);
const LeadsBoardPage = lazyRoute(
  () => import("./pages/LeadsBoardPage"),
  (module) => module.LeadsBoardPage,
);
const InventoryDetailPage = lazyRoute(
  () => import("./pages/InventoryDetailPage"),
  (module) => module.InventoryDetailPage,
);
const InventoryPage = lazyRoute(
  () => import("./pages/InventoryPage"),
  (module) => module.InventoryPage,
);
const AccountPage = lazyRoute(
  () => import("./pages/account/AccountPage"),
  (module) => module.AccountPage,
);
const PeoplePage = lazyRoute(
  () => import("./pages/PeoplePage"),
  (module) => module.PeoplePage,
);
const NotificationSettingsPage = lazyRoute(
  () => import("./pages/NotificationSettingsPage"),
  (module) => module.NotificationSettingsPage,
);
const SettingsPage = lazyRoute(
  () => import("./pages/SettingsPage"),
  (module) => module.SettingsPage,
);
const BusinessHoursSettingsPage = lazyRoute(
  () => import("./pages/BusinessHoursSettingsPage"),
  (module) => module.BusinessHoursSettingsPage,
);
const ChannelsSettingsPage = lazyRoute(
  () => import("./pages/SetupPage"),
  (module) => module.ChannelsSettingsPage,
);
const SetupChannelsOAuthRedirect = lazyRoute(
  () => import("./pages/SetupPage"),
  (module) => module.SetupChannelsOAuthRedirect,
);
const TagsSettingsPage = lazyRoute(
  () => import("./pages/SetupPage"),
  (module) => module.TagsSettingsPage,
);
const ChannelEditPage = lazyRoute(
  () => import("./pages/setup/ChannelEditPage"),
  (module) => module.ChannelEditPage,
);
const AutomationsSettingsPage = lazyRoute(
  () => import("./pages/AutomationsSettingsPage"),
  (module) => module.AutomationsSettingsPage,
);
const BookingPage = lazyRoute(
  () => import("./pages/BookingPage"),
  (module) => module.BookingPage,
);
const CalendarSettingsPage = lazyRoute(
  () => import("./pages/CalendarSettingsPage"),
  (module) => module.CalendarSettingsPage,
);
const DevelopersSettingsPage = lazyRoute(
  () => import("./pages/DevelopersSettingsPage"),
  (module) => module.DevelopersSettingsPage,
);
const TasksPage = lazyRoute(
  () => import("./pages/TasksPage"),
  (module) => module.TasksPage,
);
const TelephonyPage = lazyRoute(
  () => import("./pages/TelephonyPage"),
  (module) => module.TelephonyPage,
);

function RoutePending() {
  return (
    <div
      className="flex flex-1 flex-col gap-6 p-4 md:p-6"
      role="status"
      aria-label="Loading page"
    >
      <div className="space-y-3 border-b pb-5">
        <div className="h-3 w-24 rounded-control bg-muted" />
        <div className="h-7 w-48 rounded-control bg-muted" />
      </div>
      <div className="space-y-3" aria-hidden="true">
        <div className="h-11 rounded-control bg-muted" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
        <div className="h-16 rounded-control border bg-surface-subtle" />
      </div>
      <span className="sr-only">Loading page…</span>
    </div>
  );
}

export function App() {
  // A7 — the public tokenized booking page is session-FREE by design:
  // it renders before any auth bootstrap, so a customer clicking the
  // texted link never sees a spinner-then-sign-in wall. Checked via
  // location (not a <Route>) because everything below is gated on the
  // /me query, which must not run for this page.
  const isPublicBooking = window.location.pathname.startsWith("/book/");

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: authApi.me,
    enabled: !isPublicBooking,
    // 401 means "no session" — retrying won't change that. Anything
    // else (network blip, deploy window 500) deserves one retry before
    // we alarm the user.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 1,
  });

  if (isPublicBooking) {
    return (
      <Routes>
        <Route path="/book/:token" element={<BookingPage />} />
      </Routes>
    );
  }

  if (meQuery.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageLoading message="Loading Lumika…" />
      </div>
    );
  }

  // A failed bootstrap is not the same as being signed out: showing the
  // Google button for a transient 500 sends users through a pointless
  // re-auth. Only a 401/403 falls through to sign-in.
  if (
    meQuery.isError &&
    !(meQuery.error instanceof ApiError && (meQuery.error.status === 401 || meQuery.error.status === 403))
  ) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-foreground">
        <PageError
          title="Couldn't load Lumika"
          description="The app couldn't reach the server. Check your connection and try again."
          action={<Button onClick={() => void meQuery.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  // Unauthenticated. The login picker is reachable here because login
  // bounces to it with only a short-lived choice cookie (no session yet);
  // every other path falls through to sign-in.
  if (!meQuery.data) {
    return (
      <Routes>
        <Route path="/select-dealership" element={<SelectDealershipPage />} />
        <Route path="*" element={<SignInPage />} />
      </Routes>
    );
  }

  return (
    <>
      <Routes>
        <Route element={<AppShell me={meQuery.data} />}>
          <Route
            index
            element={
              <RestorePostLoginPath
                defaultPath={defaultLandingForRole(
                  primaryRoleForMemberships(meQuery.data.memberships),
                )}
              />
            }
          />
          <Route path="home" element={<HomePage />} />
          <Route path="insights" element={<InsightsPage />} />
          <Route path="inbox" element={<InboxLayout />}>
            <Route path=":leadId" element={<LeadDetailPage />} />
          </Route>
          <Route path="leads" element={<LeadsBoardPage />} />
          <Route path="contacts" element={<ContactsLayout />}>
            <Route path=":contactId" element={<Contact360Page />} />
          </Route>
          <Route path="calendar" element={<AppointmentsPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="inventory/:vin" element={<InventoryDetailPage />} />
          {/* The personal account is its own top-level surface (reached from
              the user menu, not the Settings tab bar) — it lives in the app
              shell but deliberately outside SettingsLayout's dealership chrome,
              the Vercel split between "your account" and "business settings". */}
          <Route path="account" element={<AccountPage />} />
          {/* Settings folds in AI config, channel Setup, Telephony, and
              Team management as sub-sections — see SettingsLayout. */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<SettingsPage />} />
            {/* Account moved out to /account — redirect stale deep-links
                (bookmarks, the old user-menu target) so they don't 404. */}
            <Route
              path="account"
              element={<Navigate to="/account" replace />}
            />
            <Route path="business-hours" element={<BusinessHoursSettingsPage />} />
            <Route path="channels">
              <Route index element={<ChannelsSettingsPage />} />
              <Route path=":channelId" element={<ChannelEditPage />} />
            </Route>
            <Route path="telephony/*" element={<TelephonyPage />} />
            <Route path="tags" element={<TagsSettingsPage />} />
            {/* Integrations setup is admin-only — redirect any stale
                deep-links back to the Settings index. */}
            <Route
              path="integrations"
              element={<Navigate to="/settings" replace />}
            />
            <Route path="notifications" element={<NotificationSettingsPage />} />
            <Route path="calendar" element={<CalendarSettingsPage />} />
            <Route path="team" element={<PeoplePage />} />
            <Route path="automations" element={<AutomationsSettingsPage />} />
            <Route path="developers" element={<DevelopersSettingsPage />} />
          </Route>
          {/* Deep-link compatibility for the old standalone routes. */}
          <Route
            path="appointments"
            element={<Navigate to="/calendar" replace />}
          />
          <Route path="people" element={<Navigate to="/settings/team" replace />} />
          {/* The Meta OAuth popup is 302'd by the backend to the pre-move
              /setup/channels path with result params — forward them intact
              so the handler on the channels page can show the outcome. */}
          <Route path="setup/channels" element={<SetupChannelsOAuthRedirect />} />
          <Route path="setup" element={<Navigate to="/settings/channels" replace />} />
          <Route path="telephony/*" element={<LegacyTelephonyRedirect />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
}

/** Land post-login on the deep link stashed by SignInPage, if any. */
function RestorePostLoginPath({ defaultPath }: { defaultPath: string }) {
  // Lazy useState keeps the render pure (StrictMode double-invokes
  // components — a render-phase removeItem would discard the stash);
  // clearing happens in the commit phase, where re-runs are idempotent.
  const [stashed] = useState(() =>
    window.sessionStorage.getItem(POST_LOGIN_PATH_KEY),
  );
  useEffect(() => {
    window.sessionStorage.removeItem(POST_LOGIN_PATH_KEY);
  }, []);
  return <Navigate to={stashed?.startsWith("/") ? stashed : defaultPath} replace />;
}

/** Old bookmarks point at /telephony/<accountId> — keep the id. */
function LegacyTelephonyRedirect() {
  const splat = useParams()["*"];
  return (
    <Navigate
      to={splat ? `/settings/telephony/${splat}` : "/settings/telephony"}
      replace
    />
  );
}

// A real 404 beats silently teleporting to the inbox: dealers following
// a dead link should learn the link is dead, not think the app glitched.
function NotFoundPage() {
  return (
    <div className="grid flex-1 place-items-center p-8">
      <PageError
        title="Page not found"
        description="The link you followed doesn't exist (anymore)."
        action={
          <Button asChild>
            <Link to="/inbox">Go to Inbox</Link>
          </Button>
        }
      />
    </div>
  );
}
